import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { COUNTRIES, codeToFlag } from "../../lib/countries";
import { userService } from "../../services/userService";
import "./AddressRequiredModal.css";

interface AddressRequiredModalProps {
  /** Called after the address has been successfully saved */
  onSaved: () => void;
  /** Called when the user closes/dismisses the modal without saving */
  onClose: () => void;
}

interface AddressDraft {
  street: string;
  number: string;
  addition: string;
  zip: string;
  city: string;
  country: string;
  iban: string;
}

const EMPTY: AddressDraft = {
  street: "",
  number: "",
  addition: "",
  zip: "",
  city: "",
  country: "",
  iban: "",
};

export default function AddressRequiredModal({
  onSaved,
  onClose,
}: AddressRequiredModalProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [draft, setDraft] = useState<AddressDraft>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSave =
    draft.street.trim().length > 0 &&
    draft.number.trim().length > 0 &&
    draft.zip.trim().length > 0 &&
    draft.city.trim().length > 0 &&
    draft.country.trim().length > 0 &&
    draft.iban.trim().length > 0;
  // addition (bus/box) is optional — not included in canSave

  const set = (field: keyof AddressDraft) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setDraft((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      await userService.updateProfile({
        street: draft.street.trim(),
        number: draft.number.trim(),
        box: draft.addition.trim() || null,
        zip: draft.zip.trim(),
        city: draft.city.trim(),
        country: draft.country.trim(),
        iban: draft.iban.trim() || undefined,
      });
      onSaved();
    } catch {
      setError(t("addressModal.error"));
    } finally {
      setSaving(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  const lang = i18n.language?.split("-")[0] as "nl" | "en" | "fr";
  const countryLabel = (c: typeof COUNTRIES[number]) =>
    lang === "fr" ? c.fr : lang === "en" ? c.en : c.nl;

  return (
    <div className="arm-backdrop" onClick={handleBackdropClick} role="dialog" aria-modal="true">
      <div className="arm-panel">
        {/* Close button */}
        <button className="arm-close" onClick={onClose} aria-label="Sluiten">
          ✕
        </button>

        {/* Icon */}
        <div className="arm-icon" aria-hidden="true">
          📍
        </div>

        <h2 className="arm-title">{t("addressModal.title")}</h2>
        <p className="arm-subtitle">{t("addressModal.subtitle")}</p>

        <form
          className="arm-form"
          onSubmit={(e) => {
            e.preventDefault();
            handleSave();
          }}
        >
          {/* Street + Number + Addition (bus, optional) */}
          <div className="arm-row">
            <div className="arm-field arm-field--grow">
              <label className="arm-label">{t("addressModal.street")}</label>
              <input
                className="arm-input"
                type="text"
                placeholder={t("addressModal.streetPlaceholder")}
                value={draft.street}
                onChange={set("street")}
                autoFocus
              />
            </div>
            <div className="arm-field arm-field--narrow">
              <label className="arm-label">{t("addressModal.number")}</label>
              <input
                className="arm-input"
                type="text"
                placeholder={t("addressModal.numberPlaceholder")}
                value={draft.number}
                onChange={set("number")}
              />
            </div>
            <div className="arm-field arm-field--narrow">
              <label className="arm-label">{t("settings.shippingPage.boxOptional", "Bus")}</label>
              <input
                className="arm-input"
                type="text"
                placeholder={t("addressModal.additionPlaceholder", "Bus")}
                value={draft.addition}
                onChange={set("addition")}
              />
            </div>
          </div>

          {/* Zip + City */}
          <div className="arm-row">
            <div className="arm-field arm-field--narrow">
              <label className="arm-label">{t("addressModal.zip")}</label>
              <input
                className="arm-input"
                type="text"
                placeholder={t("addressModal.zipPlaceholder")}
                value={draft.zip}
                onChange={set("zip")}
              />
            </div>
            <div className="arm-field arm-field--grow">
              <label className="arm-label">{t("addressModal.city")}</label>
              <input
                className="arm-input"
                type="text"
                placeholder={t("addressModal.cityPlaceholder")}
                value={draft.city}
                onChange={set("city")}
              />
            </div>
          </div>

          {/* Country */}
          <div className="arm-field">
            <label className="arm-label">{t("addressModal.country")}</label>
            <select
              className="arm-input arm-select"
              value={draft.country}
              onChange={set("country")}
              required
            >
              <option value="">—</option>
              {COUNTRIES.map((c) => (
                <option key={c.code} value={c.code}>
                  {codeToFlag(c.code)} {countryLabel(c)}
                </option>
              ))}
            </select>
          </div>

          {/* IBAN (verplicht) */}
          <div className="arm-field">
            <label className="arm-label">IBAN</label>
            <input
              className="arm-input"
              type="text"
              placeholder={t("addressModal.ibanPlaceholder", "BE68 5390 0754 7034")}
              value={draft.iban}
              onChange={set("iban")}
              required
            />
          </div>

          {error && <p className="arm-error">{error}</p>}

          <button
            type="submit"
            className="arm-btn-save"
            disabled={!canSave || saving}
          >
            {saving ? t("addressModal.saving") : t("addressModal.save")}
          </button>
        </form>

        <button
          className="arm-btn-settings"
          onClick={() => {
            onClose();
            navigate("/settings/shipping");
          }}
        >
          {t("addressModal.goToSettings")}
        </button>
      </div>
    </div>
  );
}
